import { useState } from 'react';
import { useModalState } from '../../../../../../../../../hooks/useModalState';
import LangEnum from '../../../../../../../../../lang/LangEnum';
import { useFindCatalogAccessEntities } from '../../../../../../../../../services/modules/catalogs/catalogAccess.queries';
import { CATALOG_ACCESS_ENDPOINT_BY_FIELD } from '../../../../../../../../../services/modules/catalogs/catalogAccess.repository';
import { CatalogProductsTestIds } from '../../../../../../../../../utils/testIds/CatalogProducts.testIds';
import { CATALOG_CREATION_ACCESS_TABS } from '../../../../../../../creation/catalogCreationAccess.consts';
import { CatalogCreationAccessFieldName } from '../../../../../../../creation/catalogCreationAccess.types';
import { resolveCatalogCreationAccessEmptyLabel } from '../../../../../../../creation/catalogCreationAccess.utils';
import {
  CATALOG_ACCESS_PREVIEW_LIMIT,
  CATALOG_ACCESS_TAB_LABEL_BY_FIELD,
} from '../catalogAccessSection.consts';
import { CatalogAccessCountByField } from '../catalogAccessSection.types';
import { resolveCatalogAccessEntityName } from '../catalogAccessSection.utils';

const [FIRST_ACCESS_TAB] = CATALOG_CREATION_ACCESS_TABS;

interface UseCatalogAccessSectionParams {
  catalogId: string;
}

export const useCatalogAccessSection = ({ catalogId }: UseCatalogAccessSectionParams) => {
  const [selectedField, setSelectedField] = useState<CatalogCreationAccessFieldName>(
    FIRST_ACCESS_TAB.field,
  );
  const { isOpen: isEditModalOpen, openModal, closeModal } = useModalState();

  const previewParams = { catalogId, limit: CATALOG_ACCESS_PREVIEW_LIMIT };

  const classificationPermissionsQuery = useFindCatalogAccessEntities({
    ...previewParams,
    field: 'classificationPermissions',
  });
  const customersQuery = useFindCatalogAccessEntities({ ...previewParams, field: 'customers' });
  const salesRepresentativesQuery = useFindCatalogAccessEntities({
    ...previewParams,
    field: 'salesRepresentatives',
  });

  const queryByField = {
    classificationPermissions: classificationPermissionsQuery,
    customers: customersQuery,
    salesRepresentatives: salesRepresentativesQuery,
  };

  const countByField: CatalogAccessCountByField = {
    classificationPermissions: classificationPermissionsQuery.data?.count ?? 0,
    customers: customersQuery.data?.count ?? 0,
    salesRepresentatives: salesRepresentativesQuery.data?.count ?? 0,
  };

  const selectedQuery = queryByField[selectedField];
  const isLoading = selectedQuery.isLoading;
  const hasLoadError = selectedQuery.isError;

  const entityNames = (selectedQuery.data?.data ?? []).map((entity) =>
    resolveCatalogAccessEntityName(
      entity,
      CATALOG_ACCESS_ENDPOINT_BY_FIELD[selectedField].nameField,
    ),
  );

  const tabs = CATALOG_CREATION_ACCESS_TABS.map(({ field }) => ({
    id: field,
    label: CATALOG_ACCESS_TAB_LABEL_BY_FIELD[field],
    secondaryLabel: countByField[field],
    testId: `${CatalogProductsTestIds.DETAILS_ACCESS_TAB_PREFIX}${field}`,
  }));

  const handleSelectTab = (tabId: string) => {
    const selectedTab = CATALOG_CREATION_ACCESS_TABS.find(({ field }) => field === tabId);

    if (!selectedTab) {
      return;
    }

    setSelectedField(selectedTab.field);
  };

  const hasNoEntities = Object.values(countByField).every((count) => count === 0);

  const resolveEmptyLabel = () => {
    if (hasLoadError) {
      return LangEnum.SERVER_ERROR_FAILED_LOAD_INFORMATION_MESSAGE;
    }

    return resolveCatalogCreationAccessEmptyLabel({
      hasNoEntities,
      rowCount: entityNames.length,
    });
  };

  return {
    tabs,
    countByField,
    selectedField,
    entityNames,
    isEditModalOpen,
    isLoading,
    hasLoadError,
    emptyLabel: resolveEmptyLabel(),
    handleSelectTab,
    openEditModal: openModal,
    closeEditModal: closeModal,
  };
};
